import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Loader2, Copy, ExternalLink, TrendingUp, DollarSign, Users, ShoppingBag,
  LogOut, MousePointerClick, Calendar, BarChart3, CheckCircle
} from 'lucide-react';
import { affiliateAPI, affiliateAuth } from '../services/affiliateApi';

const Stat = ({ icon: Icon, label, value, sub, color = 'teal', testid }) => (
  <Card className="capsule-card" data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-xl bg-gradient-to-br from-capsule-${color}/15 to-capsule-aqua/15`}>
          <Icon className={`w-5 h-5 text-capsule-${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AffiliateDashboardPage = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sales, setSales] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState({ link: false, code: false });

  const t = (en, es, ru) => currentLanguage === 'es' ? es : currentLanguage === 'ru' ? ru : en;
  const fmtMoney = (v) => `€${Number(v || 0).toFixed(2)}`;

  useEffect(() => {
    if (!affiliateAuth.getToken()) {
      navigate('/affiliate/login');
      return;
    }
    (async () => {
      try {
        const [dash, salesData, payoutsData] = await Promise.all([
          affiliateAPI.dashboard(),
          affiliateAPI.sales(1, 25),
          affiliateAPI.payouts(),
        ]);
        setData(dash);
        setSales(salesData);
        setPayouts(payoutsData);
      } catch (err) {
        if (err.status === 401 || err.status === 403) {
          affiliateAuth.clear();
          navigate('/affiliate/login');
          return;
        }
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleLogout = async () => {
    await affiliateAPI.logout().catch(() => {});
    navigate('/affiliate/login');
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 1500);
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-capsule-bg flex items-center justify-center" data-testid="affiliate-dashboard-loading">
        <Loader2 className="w-10 h-10 text-capsule-teal animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-capsule-bg">
        <Header />
        <main className="container mx-auto px-4 py-16 text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/affiliate/login')} className="bg-capsule-teal text-white">
            {t('Go to Login', 'Ir al Inicio', 'К входу')}
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const { affiliate, totals, this_month, monthly_breakdown } = data;
  const monthlyMax = Math.max(1, ...(monthly_breakdown || []).map(m => m.commission));

  return (
    <div className="min-h-screen bg-capsule-bg">
      <SEOHead page="affiliate" customTitle="Affiliate Dashboard | PenLab Peptide" />
      <Header />
      <main className="container mx-auto px-4 py-8 md:py-12" data-testid="affiliate-dashboard">
        {/* Header strip */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              {t('Welcome back', 'Bienvenido', 'С возвращением')}, {affiliate.name}
            </h1>
            <p className="text-gray-600 text-sm mt-1">
              {t('Your real-time affiliate performance dashboard',
                 'Tu panel de rendimiento de afiliado en tiempo real',
                 'Ваш панель эффективности в реальном времени')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
              {t('Active', 'Activo', 'Активный')}
            </Badge>
            <Badge variant="secondary">
              {affiliate.commission_percent}% {t('commission', 'comisión', 'комиссия')}
            </Badge>
            <Link to="/affiliate/marketing" data-testid="open-marketing-kit-btn">
              <Button size="sm" className="bg-gradient-to-r from-capsule-teal to-capsule-aqua text-white">
                {t('Marketing Kit', 'Kit Marketing', 'Маркетинг')} →
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="affiliate-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> {t('Logout', 'Salir', 'Выйти')}
            </Button>
          </div>
        </div>

        {/* Promo code + referral link */}
        <Card className="capsule-card mb-8 bg-gradient-to-br from-capsule-teal to-capsule-aqua text-white">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs uppercase tracking-wider opacity-90 mb-2">
                  {t('Your Promo Code', 'Tu Código Promocional', 'Ваш промокод')}
                </p>
                <div className="flex items-center gap-3">
                  <p className="text-3xl md:text-4xl font-bold tracking-wider" data-testid="affiliate-promo-code">
                    {affiliate.promo_code || ', '}
                  </p>
                  <Button size="sm" variant="ghost"
                    className="bg-white/20 hover:bg-white/30 text-white"
                    onClick={() => copy(affiliate.promo_code, 'code')}
                    data-testid="copy-promo-code">
                    {copied.code ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider opacity-90 mb-2">
                  {t('Your Referral Link', 'Tu Enlace de Referencia', 'Ваша реферальная ссылка')}
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm bg-white/15 rounded-lg px-3 py-2 truncate" data-testid="affiliate-referral-link">
                    {affiliate.referral_link || ', '}
                  </code>
                  <Button size="sm" variant="ghost"
                    className="bg-white/20 hover:bg-white/30 text-white"
                    onClick={() => copy(affiliate.referral_link, 'link')}
                    data-testid="copy-referral-link">
                    {copied.link ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs opacity-80 mt-2">
                  {t('90-day cookie attribution. Earn commission on every sale.',
                     'Atribución de cookie de 90 días. Gana comisión en cada venta.',
                     'Cookie на 90 дней. Получайте комиссию с каждой продажи.')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stat tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={DollarSign} testid="stat-total-commission"
            label={t('Total Earned', 'Total Ganado', 'Всего заработано')}
            value={fmtMoney(totals.commission)}
            sub={`${totals.sales} ${t('sales', 'ventas', 'продаж')}`}
            color="teal" />
          <Stat icon={ShoppingBag} testid="stat-total-revenue"
            label={t('Revenue Generated', 'Ingresos Generados', 'Выручка')}
            value={fmtMoney(totals.revenue)}
            color="aqua" />
          <Stat icon={MousePointerClick} testid="stat-total-clicks"
            label={t('Referral Clicks', 'Clics de Referencia', 'Клики')}
            value={totals.clicks}
            sub={`${totals.conversion_rate}% ${t('conversion', 'conversión', 'конверсия')}`}
            color="teal" />
          <Stat icon={Calendar} testid="stat-month-commission"
            label={t('This Month', 'Este Mes', 'В этом месяце')}
            value={fmtMoney(this_month.commission)}
            sub={`${this_month.sales} ${t('sales', 'ventas', 'продаж')}`}
            color="aqua" />
        </div>

        {/* Two-col: Monthly chart + Payout summary */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <Card className="capsule-card lg:col-span-2" data-testid="monthly-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-capsule-teal" />
                {t('Last 12 Months Performance', 'Últimos 12 Meses', 'Последние 12 месяцев')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!monthly_breakdown || monthly_breakdown.length === 0) ? (
                <p className="text-center text-gray-500 py-8">
                  {t('No sales data yet. Share your link to get started!',
                     'Aún no hay ventas. ¡Comparte tu enlace!',
                     'Пока нет данных. Поделитесь ссылкой!')}
                </p>
              ) : (
                <div className="space-y-2">
                  {monthly_breakdown.map((m) => (
                    <div key={m.month} className="flex items-center gap-3" data-testid={`month-row-${m.month}`}>
                      <span className="text-xs text-gray-600 w-16 font-mono">{m.month}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-7 relative overflow-hidden">
                        <div className="absolute left-0 top-0 h-full bg-gradient-to-r from-capsule-teal to-capsule-aqua rounded-full transition-all"
                          style={{ width: `${(m.commission / monthlyMax) * 100}%` }} />
                        <span className="absolute inset-0 flex items-center justify-end pr-3 text-xs font-medium text-gray-700">
                          {fmtMoney(m.commission)} · {m.sales} {t('sales', 'ventas', 'продаж')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="capsule-card" data-testid="payout-summary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-capsule-teal" />
                {t('Payouts', 'Pagos', 'Выплаты')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('Total earned', 'Total ganado', 'Заработано')}</span>
                <span className="font-semibold">{fmtMoney(payouts?.summary?.total_earned)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t('Paid out', 'Pagado', 'Выплачено')}</span>
                <span className="font-semibold text-emerald-600">{fmtMoney(payouts?.summary?.total_paid)}</span>
              </div>
              <div className="border-t pt-3 flex justify-between">
                <span className="font-medium">{t('Outstanding', 'Pendiente', 'К выплате')}</span>
                <span className="font-bold text-capsule-teal" data-testid="outstanding-amount">
                  {fmtMoney(payouts?.summary?.outstanding)}
                </span>
              </div>
              {payouts?.payouts?.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs uppercase text-gray-500 tracking-wide">
                    {t('Recent payouts', 'Pagos recientes', 'Недавние выплаты')}
                  </p>
                  {payouts.payouts.slice(0, 4).map(p => (
                    <div key={p.id} className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {new Date(p.paid_at).toLocaleDateString()}
                      </span>
                      <span className="font-medium">{fmtMoney(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-gray-500 pt-2">
                {t('Payouts are processed monthly via bank transfer.',
                   'Los pagos se procesan mensualmente por transferencia bancaria.',
                   'Выплаты ежемесячно банковским переводом.')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Sales table */}
        <Card className="capsule-card mb-12">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-capsule-teal" />
              {t('Recent Sales', 'Ventas Recientes', 'Недавние продажи')}
              <Badge variant="secondary" className="ml-auto">
                {sales?.total ?? 0} {t('total', 'total', 'всего')}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(!sales?.sales || sales.sales.length === 0) ? (
              <p className="text-center text-gray-500 py-8" data-testid="no-sales">
                {t('No sales yet. Share your code to start earning!',
                   'Aún no hay ventas. ¡Comparte tu código!',
                   'Пока нет продаж. Делитесь кодом!')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sales-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                      <th className="py-3 pr-2">{t('Date', 'Fecha', 'Дата')}</th>
                      <th className="py-3 pr-2">{t('Order', 'Pedido', 'Заказ')}</th>
                      <th className="py-3 pr-2">{t('Items', 'Items', 'Товары')}</th>
                      <th className="py-3 pr-2 text-right">{t('Order Total', 'Total Pedido', 'Сумма')}</th>
                      <th className="py-3 pr-2 text-right">{t('Commission', 'Comisión', 'Комиссия')}</th>
                      <th className="py-3 pr-2">{t('Status', 'Estado', 'Статус')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.sales.map((s) => (
                      <tr key={s.id} className="border-b last:border-0">
                        <td className="py-3 pr-2 text-gray-600">
                          {new Date(s.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 pr-2 font-mono text-xs">{s.order_id?.slice(0, 8)}...</td>
                        <td className="py-3 pr-2">{s.items_count}</td>
                        <td className="py-3 pr-2 text-right font-medium">{fmtMoney(s.order_total)}</td>
                        <td className="py-3 pr-2 text-right font-bold text-capsule-teal">{fmtMoney(s.commission_amount)}</td>
                        <td className="py-3 pr-2">
                          <Badge variant="secondary" className="capitalize">{s.order_status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-center text-sm text-gray-500">
          {t('Need help?', '¿Necesitas ayuda?', 'Нужна помощь?')}{' '}
          <Link to="/contact" className="text-capsule-teal hover:underline">
            {t('Contact support', 'Contactar soporte', 'Связаться')}
          </Link>{' '}·{' '}
          <a href={affiliate.referral_link} target="_blank" rel="noopener noreferrer"
             className="text-capsule-teal hover:underline inline-flex items-center gap-1">
            {t('Test your link', 'Prueba tu enlace', 'Проверить ссылку')} <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AffiliateDashboardPage;
